import { Where } from "@/types/crm/configuration/question_conditionals";
import { QuestionWithConditionResult } from "..";

const LOG = false;

export function sortQuestionsByDependencies(
    questions: Array<QuestionWithConditionResult>
): Array<QuestionWithConditionResult> {
    LOG && console.group("Sorting questions by dependencies");

    LOG && console.log("Input order:", questions.map(q => ({
        id: q.id,
        question: q.question
    })));

    // Build dependency graph
    const dependencyGraph = new Map<string, string | null>();
    const dependents = new Map<string, Set<string>>();

    // Initialize maps
    for (const question of questions) {
        const dependencyId = extractSingleDependency(question.selected_configuration?.conditionals?.where);
        dependencyGraph.set(question.id, dependencyId);

        if (!dependents.has(question.id)) {
            dependents.set(question.id, new Set());
        }

        if (dependencyId) {
            if (!dependents.has(dependencyId)) {
                dependents.set(dependencyId, new Set());
            }
            dependents.get(dependencyId)!.add(question.id);
        }
    }

    LOG && console.log("Dependency graph:", Array.from(dependencyGraph.entries()));
    LOG && console.log("Dependents map:", Array.from(dependents.entries()).map(([k, v]) => [k, Array.from(v)]));

    // Topological sort using Kahn's algorithm
    const result: Array<QuestionWithConditionResult> = [];
    const visited = new Set<string>();
    const questionMap = new Map(questions.map(q => [q.id, q]));

    // Find all questions with no dependencies (in-degree 0)
    const queue: string[] = [];
    for (const [questionId, dependency] of dependencyGraph.entries()) {
        if (!dependency) {
            queue.push(questionId);
        }
    }

    LOG && console.log("Initial queue (no dependencies):", queue);

    while (queue.length > 0) {
        const currentId = queue.shift()!;

        if (visited.has(currentId)) continue;
        visited.add(currentId);

        const currentQuestion = questionMap.get(currentId);
        if (currentQuestion) {
            result.push(currentQuestion);
            LOG && console.log(`Added ${currentId} to result`);

            // Add all questions that depend on this one to the queue
            const currentDependents = dependents.get(currentId) || new Set();
            for (const dependentId of currentDependents) {
                // Check if all dependencies of this dependent have been visited
                const dependentDependency = dependencyGraph.get(dependentId);
                if (!dependentDependency || visited.has(dependentDependency)) {
                    queue.push(dependentId);
                    LOG && console.log(`Added ${dependentId} to queue (all dependencies satisfied)`);
                }
            }
        }
    }

    // Check for circular dependencies or missing questions
    if (result.length !== questions.length) {
        LOG && console.warn(`Warning: Only processed ${result.length} out of ${questions.length} questions`);

        // Add any remaining questions (might have circular dependencies)
        for (const question of questions) {
            if (!visited.has(question.id)) {
                result.push(question);
                LOG && console.warn(`Added unprocessed question ${question.id} - possible circular dependency`);
            }
        }
    }

    LOG && console.log("\nFinal verification:");
    for (let i = 0; i < result.length; i++) {
        const question = result[i];
        const dependencyId = extractSingleDependency(question.selected_configuration?.conditionals?.where);
        if (dependencyId) {
            const dependencyIndex = result.findIndex(q => q.id === dependencyId);
            if (dependencyIndex > i) {
                LOG && console.error(`ERROR: ${question.id} still comes before its dependency ${dependencyId}`);
            } else if (dependencyIndex === -1) {
                LOG && console.error(`ERROR: Dependency ${dependencyId} not found for ${question.id}`);
            } else {
                LOG && console.log(`✓ ${question.id} correctly comes after its dependency ${dependencyId}`);
            }
        }
    }

    LOG && console.log("Final order:", result.map((q, index) => ({
        index,
        id: q.id,
        question: q.question,
        dependency: extractSingleDependency(q.selected_configuration?.conditionals?.where) || 'none'
    })));

    LOG && console.groupEnd();
    return result;
}

function extractSingleDependency(where?: Where): string | null {
    if (!where) return null;

    for (const [key] of Object.entries(where)) {
        if (key !== 'and' && key !== 'or') {
            LOG && console.log(`Found dependency: ${key}`);
            return key; // This is a question ID
        }
    }

    if (where.and && where.and.length > 0) {
        LOG && console.log("Checking 'and' conditions for dependency");
        return extractSingleDependency(where.and[0]);
    }

    if (where.or && where.or.length > 0) {
        LOG && console.log("Checking 'or' conditions for dependency");
        return extractSingleDependency(where.or[0]);
    }

    return null;
}